import { useErrorReporter } from '@/composables/useErrorReporter'
import type { ResultList } from '@/types/components/address-checker'
import { useDebounce } from '@vueuse/core'
import {
	ref,
	watch
} from 'vue'

export function useAddressAutocompleter() {
	const { bugsnagReport } = useErrorReporter()

	const searchTerm = ref('')
	const searchPlaceId = ref('')
	const activeSearchItem = ref(-1)
	const resultList = ref<ResultList[]>([])
	const isSearchActive = ref(false)
	const dontSearch = ref(false)
	const service = ref<google.maps.places.AutocompleteService | null>(null)
	const loaderError = ref(false)
	const selectedFromList = ref(false)
	const isLoading = ref(false)
	const isGoogleMapsLoaded = ref(false)

	// Lazy load Google Maps API
	const loadGoogleMapsAPI = async () => {
		if (isGoogleMapsLoaded.value || isLoading.value) {
			return
		}

		isLoading.value = true

		try {
		// Use dynamic import instead of static import
			const { Loader } = await import('@googlemaps/js-api-loader')

			const loader = new Loader({
				apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
				version: 'quarterly',
				libraries: [ 'places' ]
			})

			await loader.importLibrary('places')
			isGoogleMapsLoaded.value = true
		} catch (error) {
			bugsnagReport({
				error: new Error('Google Maps API Error' + error),
				showToast: false
			})
			loaderError.value = true
		} finally {
			isLoading.value = false
		}
	}

	const displaySuggestions = (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
		if (!google || status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
			return
		}
		resultList.value.length = 0
		predictions
			.filter(result => result.description)
			.forEach(result => resultList.value.push({
				desc: result.description,
				id: result.place_id
			}))
	}

	const doSearch = async () => {
		// Load Google Maps API if not already loaded
		if (!isGoogleMapsLoaded.value && !loaderError.value) {
			await loadGoogleMapsAPI()
		}

		if (!google || loaderError.value) {
			return
		}

		if (!service.value) {
			const { AutocompleteService } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary
			service.value = new AutocompleteService()
		}

		if (searchTerm.value) {
			const options = {
				input: searchTerm.value,
				locationBias: {
					center: new google.maps.LatLng(39.5186, -104.7614), // Parker, CO
					radius: 80000 // 80km or ~50mi
				},
				types: [ 'address' ]
			}
			await service.value.getPlacePredictions(options, displaySuggestions)
		}
	}

	const searchFocused = async (event?: FocusEvent) => {
		if (event) {
			const target = event.target as HTMLInputElement | null
			target?.select()
		}

		// Preload Google Maps API when user focuses on the input
		if (!isGoogleMapsLoaded.value && !loaderError.value) {
			loadGoogleMapsAPI() // Don't await here to avoid blocking the UI
		}

		setTimeout(() => {
			isSearchActive.value = true
		}, 150)
	}

	const searchBlurred = (event?: FocusEvent) => {
		// Check if focus is moving to the dropdown or staying within the component
		const relatedTarget = event?.relatedTarget as HTMLElement
		if (relatedTarget?.closest('[data-autocomplete-dropdown]')) {
			return // Don't close if clicking on dropdown
		}
		// check if focus is moving to the zip code input
		if (relatedTarget?.closest('[data-zip-code-input]')) {
			return // Don't close if clicking on zip code input
		}

		setTimeout(() => {
			isSearchActive.value = false
		}, 150)
	}

	const checkActiveSearchIndex = () => {
		if (activeSearchItem.value < 0) {
			activeSearchItem.value = resultList.value.length - 1
		} else if (activeSearchItem.value >= resultList.value.length) {
			activeSearchItem.value = -1
		}
	}

	const handleKeyPress = (event: KeyboardEvent) => {
		searchFocused()
		selectedFromList.value = false
		if (event.key === 'ArrowDown') {
			activeSearchItem.value++
			checkActiveSearchIndex()
		} else if (event.key === 'ArrowUp') {
			activeSearchItem.value--
			checkActiveSearchIndex()
		} else if (event.key === 'Enter') {
			const selectedItem = resultList.value[activeSearchItem.value]
			if (
				activeSearchItem.value >= 0
				&& selectedItem
				&& typeof selectedItem.desc === 'string'
				&& typeof selectedItem.id === 'string'
			) {
				setSearchTerm(selectedItem.desc, selectedItem.id, activeSearchItem.value)
			}
		}
	}

	const setSearchTerm = (newSearchTerm: string, id: string, index: number) => {
		dontSearch.value = true
		searchTerm.value = newSearchTerm
		searchPlaceId.value = id
		selectedFromList.value = true
		if (index !== undefined) {
			activeSearchItem.value = index
		}
	}

	const clearSearchResults = () => {
		resultList.value = []
		activeSearchItem.value = -1
		searchTerm.value = ''
		searchPlaceId.value = ''
		isSearchActive.value = false
		dontSearch.value = false
	}

	const debounced = useDebounce(searchTerm, 200)

	watch(debounced, () => {
		if (!dontSearch.value) {
			doSearch()
		}
		dontSearch.value = false
	})

	return {
		setSearchTerm,
		clearSearchResults,
		searchTerm,
		activeSearchItem,
		resultList,
		isSearchActive,
		searchFocused,
		searchBlurred,
		handleKeyPress,
		searchPlaceId,
		selectedFromList,
		isLoading,
		isGoogleMapsLoaded
	}
}
